/* eslint-disable react/jsx-no-undef */
import { colors } from '@frontend/constants';
import { Fragment, useFeatureValue } from '@frontend/react';
import {
  AppShell,
  DesktopOnly,
  EnvironmentDialog,
  MaintenancePane,
  MobileLayoutProvider,
  YStack,
  isMobile,
  useIsOnMobile,
  useIsOnTablet,
} from '@frontend/web-react';
import { AppContentWrapper, AppSidebar, BottomNav, Outlet } from '@frontend/web/components';
import { PricingTerminologyProvider, QuackActionsProvider } from '@frontend/web/context';
import { isMcpFlow, useAnalytics } from '@frontend/web/hooks';
import { Env, createFileRoute, handleRedirectBeforeLoad, redirect, useRouter } from '@frontend/web/utils';
import { useMatchRoute } from '@tanstack/react-router';
import { QuackActions } from '../components/QuackActions/QuackActions';

export const SIDEBAR_WIDTH_COLLAPSED = 86;
export const SIDEBAR_WIDTH_EXPANDED = 266;

const App = () => {
  const router = useRouter();
  const matchRoute = useMatchRoute();
  const matchedSettings = !!matchRoute({ fuzzy: true, to: '/app/settings' });
  const matchedOptionSheets = !!matchRoute({ fuzzy: true, to: '/app/option-sheet/$optionSheetId' });
  const matchedDraftTaskV2Create = Boolean(
    matchRoute({ to: '/app/draft-task/create' }) || matchRoute({ to: '/app/draft-task/$draftTaskId' }),
  );
  const matchedTaskDetail = !!matchRoute({ fuzzy: true, to: '/app/task/$taskId' });
  const { track } = useAnalytics();
  const isOnTablet = useIsOnTablet();
  const isOnMobile = useIsOnMobile();
  const isOnMobileOrTablet = isOnTablet || isOnMobile;
  const shouldMinimize = Boolean(matchedSettings || isOnMobileOrTablet);
  const blockUI = false; // Disabled blocker screen for web mobile development
  const sidebarWidth = shouldMinimize ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const { value: maintenanceModeValue } = useFeatureValue('maintenance_mode');

  const onTrackAppStoreButton = (platform: MobilePlatform) => {
    track({
      event: platform === 'android' ? 'androidDownloadLinkSelected' : 'iosDownloadLinkSelected',
      topic: 'onboarding',
    });
  };

  if (blockUI) {
    return (
      <DesktopOnly
        onClickNavigateAccount={() => router.navigate({ to: '/app/settings/payments' })}
        onTrackAppStoreButton={onTrackAppStoreButton}
      />
    );
  } else if (matchedOptionSheets) {
    return (
      <AppShell
        navbar={{
          breakpoint: 'sm',
          width: sidebarWidth,
        }}
        padding="md"
      >
        <YStack className="flex h-screen overflow-y-hidden">
          <Outlet />
        </YStack>
        <EnvironmentDialog apiUrl={Env.VITE_API_BASE_URL} env={Env.VITE_ENV} />
      </AppShell>
    );
  } else if (isMobile) {
    return (
      <MobileLayoutProvider disableInternalScroll>
        <QuackActionsProvider>
          {({ actions, isSearchLoading, onStartSearch, resetActions }) => (
            <Fragment>
              <QuackActions
                actionGroups={actions}
                isSearchLoading={isSearchLoading}
                onQueryChange={onStartSearch}
                onResetQuery={resetActions}
              />
              <div
                style={{
                  backgroundColor: colors.cream[50],
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100dvh',
                  left: 0,
                  overflow: 'hidden',
                  position: 'fixed',
                  top: 0,
                  width: '100%',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                  }}
                >
                  <AppShell>
                    {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                    <AppContentWrapper className="min-h-[calc(100vh-61px)]" style={{ padding: 0 }}>
                      <Outlet />
                    </AppContentWrapper>
                    <EnvironmentDialog apiUrl={Env.VITE_API_BASE_URL} env={Env.VITE_ENV} />
                  </AppShell>
                </div>
                {!matchedTaskDetail ? <BottomNav /> : null}
              </div>
            </Fragment>
          )}
        </QuackActionsProvider>
      </MobileLayoutProvider>
    );
  } else if (maintenanceModeValue?.is_on) {
    return (
      <AppShell
        className="flex flex-col"
        navbar={{
          breakpoint: 0,
          width: sidebarWidth,
        }}
      >
        <AppContentWrapper>
          <MaintenancePane />
        </AppContentWrapper>
        <EnvironmentDialog apiUrl={Env.VITE_API_BASE_URL} env={Env.VITE_ENV} />
      </AppShell>
    );
  }

  return (
    <AppShell
      className="flex flex-col"
      navbar={{
        breakpoint: 0,
        width: matchedDraftTaskV2Create ? 0 : sidebarWidth,
      }}
    >
      <QuackActionsProvider>
        {({ actions, isSearchLoading, onStartSearch, resetActions }) => (
          <Fragment>
            <QuackActions
              actionGroups={actions}
              isSearchLoading={isSearchLoading}
              onQueryChange={onStartSearch}
              onResetQuery={resetActions}
            />
            {matchedDraftTaskV2Create ? null : <AppSidebar />}
            <AppContentWrapper>
              <Outlet />
            </AppContentWrapper>
          </Fragment>
        )}
      </QuackActionsProvider>
      <EnvironmentDialog apiUrl={Env.VITE_API_BASE_URL} env={Env.VITE_ENV} />
    </AppShell>
  );
};

const AppWithProviders = () => (
  <PricingTerminologyProvider>
    <App />
  </PricingTerminologyProvider>
);

export const Route = createFileRoute('/app')({
  beforeLoad: async ({ context, location }) => {
    if (isMcpFlow()) {
      throw redirect({
        to: '/signup/mcp/complete',
      });
    }

    handleRedirectBeforeLoad({ context, location });
  },
  component: AppWithProviders,
});
